import { defineStore } from 'pinia'
import { useFormErrorsStore } from '~/stores/useFormErrors'

export const useTableDataStore = (affix: string, suffix: string = '') =>
  defineStore(`table-${affix}`, () => {
    const { errors } = storeToRefs(useFormErrorsStore())
    const { t } = useI18n()
    const toast = useToast()

    const showError = (err: any) => {
      const apiData = err?.data || err?.response?._data || err?.response?.data
      console.log(apiData)
      if (apiData) {
        const message =
          apiData.message ||
          Object.values(apiData.errors || {})
            .flat()
            .join(', ')

        toast.add({
          title: t('forms.errors.title'),
          description: message,
          color: 'error',
        })
        return
      }

      toast.add({
        title: t('forms.errors.title'),
        description: err?.message || String(err),
        color: 'error',
      })
    }

    const permissions = ref([])
    const itemsPerPage = ref(20)
    const data = ref<any>()
    const params = ref('')
    const additionParams = ref('')

    const setParams = (parameters: string) => {
      params.value = parameters
    }
    const setAdditionParams = (parameters: string) => {
      additionParams.value = parameters
    }
    const isFinal = ref(false)

    const getData = async (clear: boolean, accumulation: boolean = false) => {
      try {
        let url = 'api/' + suffix + affix

        // формируем query string
        const queryParts: string[] = []

        if (params.value) queryParts.push(params.value.replace(/^\?/, ''))
        if (additionParams.value)
          queryParts.push(additionParams.value.replace(/^\?/, ''))

        const queryString = queryParts.length ? '?' + queryParts.join('&') : ''

        const response = await useApi(url + queryString, { method: 'GET' })
        if (!response.data) return

        if (clear) {
          data.value = response.data
          isFinal.value = false
          return
        }

        if (accumulation) {
          data.value = {
            ...data.value,
            items: [
              ...(data?.value?.items || []),
              ...(response.data.items || []),
            ],
          }
          isFinal.value = !response.data.items?.length
        } else {
          data.value = response.data
          isFinal.value = false
        }
      } catch (error) {
        showError(error)
        console.error('Failed to fetch data:', error)
      }
    }

    const create = async (data: any) => {
      try {
        const response = await useApi('api/' + suffix + affix, {
          method: 'POST',
          body: data,
        })

        if (response?.success) {
          getData(false)
          return true
        }
      } catch (error: any) {
        errors.value = error.response?._data?.errors;
        console.error('User creation failed:', error)
        return false
      }
    }

    const read = async (id: [number, string]) => {
      const url = 'api/' + suffix + affix + '/' + id
      try {
        const response = await useApi(url, {
          method: 'GET',
        })

        if (response?.success) {
          return response?.data
        }
      } catch (error) {
        showError(error)
        console.error('User creation failed:', error)
        return false
      }
    }

    const readOnce = async (
      id: number,
      addition: string,
      likeParam?: boolean
    ) => {
      const params = !likeParam ? '/' + '?warehouseTaskUuid=' + id : '/' + id
      const url = 'api/' + suffix + affix + addition + params
      try {
        const response = await useApi(url, {
          method: 'GET',
        })

        if (response?.success) {
          return response?.data
        }
      } catch (error) {
        showError(error)
        console.error('User creation failed:', error)
        return false
      }
    }

    const update = async (data: any, id: string) => {
      try {
        const response = await useApi('api/' + suffix + affix + '/' + id, {
          method: 'PUT',
          body: data,
        })
        if (response?.success) {
          getData(false)
          return true
        }
      } catch (error) {
        showError(error)
        console.error('User creation failed:', error)
        return false
      }
    }

    const remove = async (id: number) => {
      try {
        const response = await useApi(
          'api/' + suffix + affix + '/delete/' + id,
          {
            method: 'DELETE',
          }
        )
        if (response?.success) {
          getData(false)
          return true
        }
      } catch (error) {
        showError(error)
        console.error('User creation failed:', error)
        return false
      }
    }

    const importData = async (name: string, file: any[], params: any) => {
      const fileStore = useFileStore()

      try {
        if (!file || !file.length) throw new Error('Файл не выбран')

        const fileToSend = file.map((f) => ({
          name: f.name,
          type: f.type,
          size: f.size,
          lastModified: f.lastModified,
          content: f.content,
        }))

        const fileResult = await fileStore.sendFiles(fileToSend)
        const fileId = fileResult?.[0]?.id
        if (!fileId) throw new Error('Не удалось загрузить файл')

        const fullParams = { ...params, fileUuid: fileId }

        const response = await useApi(`/api/${name}/import-excel`, {
          method: 'POST',
          body: fullParams,
        })

        if (response?.success) {
          getData(false)
          return true
        }

        throw new Error('Импорт завершился с ошибкой')
      } catch (error) {
        showError(error)
        console.error('Ошибка при импорте данных:', error)
        return false
      }
    }

    const removeMany = async (uuids: string[]) => {
      if (!uuids || !uuids.length) return false

      try {
        const query = uuids
          .map((id) => `ids[]=${encodeURIComponent(id)}`)
          .join('&')
        const url = `api/${suffix}${affix}/mass-delete?${query}`

        const response = await useApi(url, { method: 'DELETE' })

        if (response?.success) {
          getData(false)
          return true
        }
      } catch (error) {
        showError(error)
        console.error('Удаление нескольких записей не удалось:', error)
        return false
      }
    }

    const reload = () => {
      isFinal.value = false
    }

    return {
      itemsPerPage,
      getData,
      data,
      params,
      permissions,
      setParams,
      create,
      read,
      update,
      remove,
      isFinal,
      reload,
      setAdditionParams,
      importData,
      readOnce,
      removeMany,
    }
  })()
